import React, { useEffect, useRef, useState } from "react";
import { Input } from "@components/atoms/Input";
import Image from "next/image";
import styles from "./CitySelectionModalMobile.module.scss";
import assetLoader from "@utils/internalImageLoder";
import Text from "@components/atoms/Text";
import ModalManager from "@components/molecules/ModalManager";
import { FALLBACK_CITIES, CITY_ALIASES } from "@constants/cities";
import CityListItem from "@components/molecules/cityListItem";
import CityGridItem from "@components/molecules/CityGridItem";
import { getCurrentPosition } from "@services/geolocationService";
import { getCities } from "@services/cityService";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { reverseGeocodeCity } from "@services/geocodingService";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { City, CitySelectionModalProps } from "../City.types";
import { useCity } from "@context/CityContext";
import { usePathname } from "next/navigation";
import { getPageNameFromUrl } from "@utils/pageNameUtils";
import { normalizeCityName } from "@utils/urlNormalization";

export default function CitySelectionModalMobile({
  selectedCity,
  isModalOpen,
  setIsModalOpen,
  handleCitySelection,
}: CitySelectionModalProps) {

  const [cities, setCities] = useState<City[]>([]);
  const { setCity: setContextCity, city: contextCity } = useCity();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [isSearchSticky, setIsSearchSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchBarHeight, setSearchBarHeight] = useState(0);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const { showLoader, hideLoader } = useGlobalLoader();
  const pathname = usePathname();

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleBackClick = () => {
    setSearch("");
    setSearchFocused(false);
    inputRef.current?.blur();
  };

  const handleCityChange = (newCity: any) => {
    setContextCity(newCity);
    closeModal();
    showLoader();
    if (handleCitySelection) {
      handleCitySelection(newCity);
    } else {
      if (pathname?.includes('price-in-')) {
        const normalizedCityName = normalizeCityName(newCity.name || '');
        const newPath = pathname.replace(/price-in-[^/]+/, `price-in-${normalizedCityName}`);
        window.location.href = newPath;
      } else {
        window.location.reload();
      }
    }
  };

  const handleUseCurrentLocation = async () => {
    setIsLocating(true);
    setLocationError("");

    // Check for basic requirements
    if (!navigator.geolocation) {
      setLocationError("Location services are not supported on this device.");
      setIsLocating(false);
      return;
    }

    if (!cities || cities.length === 0) {
      setLocationError("Please wait for cities to load before using location.");
      setIsLocating(false);
      return;
    }

    // Track click on use current location
    const currentSelectedCity = getSelectedCityClient();
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_current_location",
        city_name: currentSelectedCity?.name || "",
      },
    });

    try {
      const { lat, lon } = await getCurrentPosition();
      const cityName = await reverseGeocodeCity(lat, lon);

      if (!cityName) {
        throw new Error(
          "Could not determine your city from the location. Please select manually."
        );
      }

      // Try to match cityName to your cities list (case-insensitive, alias-aware)
      const normalizedCity = cityName.toLowerCase();
      let match = cities.find((c) => c.name?.toLowerCase() === normalizedCity);

      if (!match && CITY_ALIASES[normalizedCity]) {
        const alias = CITY_ALIASES[normalizedCity];
        match = cities.find((c) => c.name?.toLowerCase() === alias);
      }

      if (match) {
        // Track current location fetched
        await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
          additionalProps: {
            action_type: "tap_btn",
            action_name: "tap_btn_location_fetched",
            selected_city: match.name,
            city_name: match.name || "",
          },
        });
        handleSelect(match);
      } else {
        setLocationError(
          `"${cityName}" is not available in our service area. Please select your city manually.`
        );
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : "Could not get your location.";
      setLocationError(errorMessage);
    } finally {
      setIsLocating(false);
    }
  };

  useEffect(() => {
    async function fetchCities() {
      setLoading(true);
      const activeCities = await getCities();
      setCities(activeCities.length ? activeCities : FALLBACK_CITIES);
      setLoading(false);

      if (isModalOpen) {
        const currentSelectedCity = getSelectedCityClient();
        await trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
          pageType: getPageNameFromUrl(pathname),
          additionalProps: {
            view_type: "view_page",
            view_name: "view_list_of_cities",
            city_name: currentSelectedCity?.name || "",
          },
        });
      }
    }
    fetchCities();
  }, [isModalOpen]);

  const filteredCities = search
    ? cities.filter((c) => c.name?.toLowerCase().includes(search.toLowerCase()))
    : cities;

  const popularCities = cities.filter(
    (city) =>
      city?.city_config?.is_live &&
      city?.city_config?.logo_image &&
      city?.city_config?.sorting_rank > 0
  );

  const handleSelect = (city: City) => {
    handleCityChange(city);
    closeModal();
    setSearch("");
  };

  useEffect(() => {
    if (sentinelRef.current) {
      setSearchBarHeight(sentinelRef.current.offsetHeight);
    }
  }, [isModalOpen]);

  const handleScroll = () => {
    if (!sentinelRef.current || !scrollRef.current) return;
    const sentinelRect = sentinelRef.current.getBoundingClientRect();
    const scrollRect = scrollRef.current.getBoundingClientRect();
    setIsSearchSticky(sentinelRect.top <= scrollRect.top + 30);
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("resize", handleScroll);
    };
  }, [isModalOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputContainerRef.current &&
        !inputContainerRef.current.contains(event.target as Node)
      ) {
        setSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <ModalManager
      isOpen={isModalOpen}
      onClose={closeModal}
      preventScroll={false}
      customClass={styles.modal}
      modalBodyClass={styles.modalBody}
      showClose={false}
    >
      <div className={styles.modalContainer}>
        <div
          className={styles.scrollableContent}
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {
            !searchFocused && (
              <div className={`${styles.modalHeader} ${isSearchSticky ? "" : ""}`}>
                <div className={styles.backIconWrapper}>
                  <button className={styles.modalbackIcon} onClick={closeModal} title="Close modal">
                    {!isSearchSticky && (
                      <Image
                        src={`${assetLoader()}/icons/CitySelection/back-icon.svg`}
                        alt="Back"
                        width={24}
                        height={24}
                        loading="lazy"
                      />
                    )}
                  </button>
                </div>
                <Text className={styles.modalTitle}>Select your city</Text>
                <Text className={styles.subHeading}>
                  ACKO Drive is available in{" "}
                  <span className={styles.subHeadingCity}>1000+ cities</span>
                </Text>
                <div ref={sentinelRef} style={{ height: 1 }} />
              </div>
            )
          }
          <div ref={inputContainerRef} className={styles.searchStickyWrapper}>
            {isSearchSticky && !searchFocused && (
              <button className={styles.modalbackIcon} onClick={closeModal} title="Close modal">
                <Image
                  src={`${assetLoader()}/icons/CitySelection/back-icon.svg`}
                  alt="Back"
                  width={24}
                  height={24}
                  loading="lazy"
                />
              </button>
            )}

            {searchFocused ? (
              <Image
                src={`${assetLoader()}/icons/CitySelection/Chevron_left_dark.svg`}
                alt="Back"
                className={styles.backIcon}
                width={24}
                height={24}
                loading="lazy"
                onClick={handleBackClick}
              />
            ) : (
              <Image
                src={`${assetLoader()}/icons/CitySelection/search-icon.svg`}
                alt="Search"
                className={`${styles.searchIcon} ${isSearchSticky ? styles.stickySearchIcon : ""
                  }`}
                width={20}
                height={20}
                loading="lazy"
                onClick={async () => {
                  inputRef.current?.focus();

                  // Track click on search
                  const currentSelectedCity = getSelectedCityClient();
                  await trackEventWithCommonProps(
                    "action",
                    EventType.TRACK_EVENT,
                    {
                      additionalProps: {
                        action_type: "tap_btn",
                        action_name: "tap_btn_city_search",
                        city_name: currentSelectedCity?.name || "",
                      },
                    }
                  );
                }}
              />
            )}
            <Input
              ref={inputRef}
              type="text"
              placeholder="Which city are you looking for?"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
              autoFocus={false}
              onFocus={async () => {
                setSearchFocused(true);

                // Track click on search (when focusing on input)
                const currentSelectedCity = getSelectedCityClient();
                await trackEventWithCommonProps(
                  "action",
                  EventType.TRACK_EVENT,
                  {
                    additionalProps: {
                      action_type: "tap_btn",
                      action_name: "tap_btn_city_search",
                      city_name: currentSelectedCity?.name || "",
                    },
                  }
                );
              }}
            />
          </div>
          {
            !searchFocused && (
              <div className={styles.useLocationWrapper}>
                <button
                  className={styles.useLocationButton}
                  onClick={handleUseCurrentLocation}
                  disabled={isLocating}
                >
                  <Image
                    src={`${assetLoader()}/icons/CitySelection/location.svg`}
                    width={20}
                    height={20}
                    alt="location"
                  />
                  {isLocating ? "Detecting..." : "Use current location"}
                </button>
                {locationError && (
                  <div className={styles.locationError}>{locationError}</div>
                )}
              </div>
            )
          }

          <div className={styles.citySection}>
            {loading ? (
              <div className={styles.loading}>Loading cities...</div>
            ) : error ? (
              <div className={styles.error}>Failed to load cities.</div>
            ) : search && filteredCities.length === 0 ? (
              <div className={styles.emptyState}>
                <Image
                  src={`${assetLoader()}/icons/CitySelection/NoVehicle.svg`}
                  width={104}
                  height={104}
                  alt="location"
                  className={styles.NoVehicleIcon}
                />
                <div className={styles.emptyHeading}>Location not found</div>
                <div className={styles.emptySubHeading}>
                  We couldn’t find that location. Please check the spelling or
                  try a nearby city.
                </div>
              </div>
            ) : (
              <>
                {!search && popularCities?.length > 0 && (
                  <div className={styles.sectionContainer}>
                    <h3 className={styles.sectionTitle}>Popular Cities</h3>
                    <div className={styles.cityGrid}>
                      {popularCities.map((city) => (
                        <CityGridItem
                          key={city.id}
                          city={city}
                          selectedCity={selectedCity}
                          onSelect={handleSelect}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className={styles.sectionContainer}>
                  <h3 className={styles.sectionTitle}>All Cities</h3>
                  <div className={styles.cityList}>
                    {filteredCities?.map((city) => (
                      <CityListItem
                        key={city.id}
                        city={city}
                        selectedCity={selectedCity}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalManager>
  );
}
